import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import AsyncCreatableSelect from 'react-select/async-creatable';

import useGoogleDriveSearch from '../../hooks/useGoogleDriveSearch';

import { BeforeIcon } from '../../components/page/CategoryFolderTitle';

import UniSearch from '../../api/uniSearch';
import { EVENTS, ICON, RESOURCE_TYPE } from '../../utilities/CONSTANTS';
import { KEYCODE } from '../utilities/CONSTANTS';
import NotionPage from '../../schemas/NotionPageSchema';
import { hasUrlScheme, findLink } from '../utilities/linkUtilities';

const isGDriveFile = resource => resource.type === RESOURCE_TYPE.GOOGLE_DOCS;

const getResourceIcon = (resource) => {
    if (!resource || resource.__isNew__) return null;
    return resource.getIcon();
};

const formatOptionLabel = (resource, data) => (!resource.__isNew__ ? (
    <div>
        <div>
            {resource.getTitle()}
        </div>
        {data.context === 'menu' && (
            <div style={{ color: '#ccc' }}>
                {resource.getSubtitle()}
            </div>
        )}
    </div>
) : (
    <div>
        <div>
            {resource.value}
        </div>
    </div>
));

const getOptionValue = (resource) => {
    if (isGDriveFile(resource)) {
        return resource.getUrl();
    } if (resource.__isNew__) {
        return resource.value;
    }
    return resource.id;
};

const customStyles = {
    option: (styles, { data }) => {
        const icon = getResourceIcon(data);
        return {
            ...styles,
            ...BeforeIcon(icon),
        };
    },
    singleValue: (styles, { data }) => {
        const icon = getResourceIcon(data);
        return {
            ...styles,
            ...BeforeIcon(icon),
        };
    },
};

const ChoosePageOrLinkDialog = (props) => {
    const [selectedResource, setSelectedResource] = useState();
    const selectRef = React.createRef();

    const googleDriveSearch = useGoogleDriveSearch();
    const hasGoogle = googleDriveSearch !== false;

    useEffect(() => { selectRef.current.focus(); }, []);

    const loadOptions = (searchQuery) => new Promise((resolve, reject) => {
        const tettraSearch = new UniSearch(window.algoliaAppId, window.algoliaSearchKey);

        const promises = [];

        if (window.algoliaSearchKey && tettraSearch) {
            promises.push(tettraSearch(searchQuery));
        }

        if (googleDriveSearch) {
            promises.push(googleDriveSearch(searchQuery));
        }

        if (window.hasNotion) {
            promises.push(NotionPage.Search(searchQuery, 10));
        }

        Promise.allSettled(promises)
            .then((results) => {
                let allResources = [];
                results.forEach((result) => {
                    if (result.status === 'rejected') {
                        console.error(result.reason);
                        return;
                    }
                    const response = result.value;
                    if (Array.isArray(response)) {
                        allResources = allResources.concat(response);
                    } else if (response.files) {
                        allResources = allResources.concat(response.files);
                    }
                });
                resolve(allResources);
            });
    });

    const onClickModal = ({ target }) => {
        const className = target.getAttribute('class');
        if (className && className.includes('react-modal open')) {
            props.closeModal();
        }
    };

    const onClickChoose = () => {
        if (!selectedResource) return;

        const isPasted = false;
        const fromDialog = true;
        if (selectedResource.__isNew__) {
            props.insertLink(selectedResource.value, fromDialog);
        } else if (selectedResource) {
            props.insertInlineReference(selectedResource, isPasted, fromDialog);
        }
        props.closeModal();
    };

    const isValidNewOption = (optionText) => hasUrlScheme(optionText) || findLink.match(optionText);

    const formatCreateLabel = (customLink) => (
        <div>
            Add link:
            {' '}
            <span className="link">{customLink}</span>
        </div>
    );

    const onCreateOption = (customLink) => {
        const label = customLink;
        const value = hasUrlScheme(customLink) ? customLink : `http://${customLink}`;
        const __isNew__ = true;
        setSelectedResource({ label, value, __isNew__ });
    };

    const handleKeyDown = (e, choosePage) => {
        if (e.which === KEYCODE.enter) {
            if (choosePage) {
                return onClickChoose();
            }
            return props.closeModal();
        }
    };

    return (
        <div className="react-modal open" onMouseDown={onClickModal}>
            <div className="modal-content p2">
                <h2>Attach a Tettra page or link</h2>
                <div className="mt2">
                    <AsyncCreatableSelect
                        ref={selectRef}
                        defaultOptions
                        openMenuOnFocus
                        isClearable
                        tabIndex={0}
                        isValidNewOption={isValidNewOption}
                        onCreateOption={onCreateOption}
                        createOptionPosition="first"
                        className="block col-12 mb1"
                        styles={customStyles}
                        value={selectedResource}
                        onChange={selectedResource => setSelectedResource(selectedResource)}
                        loadOptions={loadOptions}
                        loadingMessage={() => 'Loading pages...'}
                        placeholder="Choose page or add link"
                        getOptionValue={getOptionValue}
                        formatCreateLabel={formatCreateLabel}
                        formatOptionLabel={formatOptionLabel}
                    />
                </div>
                <div className="right-align mt2">
                    <button className="btn btn-secondary mr1" role="button" onClick={props.closeModal} onKeyDown={(e) => handleKeyDown(e, false)} tabIndex={0}>Close</button>
                    <button className="btn btn-primary" role="button" onMouseDown={onClickChoose} onKeyDown={(e) => handleKeyDown(e, true)} tabIndex={0}>Choose</button>
                </div>
            </div>
        </div>
    );
};

const ChoosePageOrLinkDialogWrapper = (props) => {
    const [showModal, setShowModal] = useState();

    const handleAttachPageEvent = () => {
        setShowModal(true);
    };

    useEffect(() => {
        window.addEventListener(EVENTS.SHOW_CHOOSE_PAGE_DIALOG, handleAttachPageEvent);
        return () => (window.removeEventListener(EVENTS.SHOW_CHOOSE_PAGE_DIALOG, handleAttachPageEvent));
    }, []);

    return !showModal
        ? null
        : (createPortal(<ChoosePageOrLinkDialog {...props} closeModal={() => setShowModal(false)} />, document.body));
};

export default ChoosePageOrLinkDialogWrapper;
